import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from "react";
import { useAuth } from "../../../auth/AuthContext";
import {
  createJobComment,
  deleteJobComment,
  getJobComments,
  getMentionableUsers,
} from "../../../lib/api";
import { formatAttachmentDate } from "../lib/attachmentKind";
import type { CommentRecord, MentionableUser } from "../types";
import { Markdown } from "./Markdown";
import form from "./NewJobForm.module.css";
import styles from "./CommentsPage.module.css";

/** How many candidates the @-autocomplete shows at once. */
const MENTION_MATCH_LIMIT = 8;

export interface CommentsPageProps {
  /** Persisted job id — this page only renders in edit mode. */
  jobId: string;
}

/**
 * The Comments page — the job's conversation (job-collaboration epic).
 *
 * Loads live comments and the mentionable pool for the job, renders bodies as
 * markdown ({@link Markdown}), and posts from a composer with a small insert
 * toolbar and an @-autocomplete over `GET /api/jobs/:id/mentionable`. Comments
 * are server data, not draft fields: posting and deleting hit the API directly
 * (the documents-section pattern), so nothing here participates in the form's
 * submit — every control is `type="button"` and Enter stays a newline.
 */
export function CommentsPage({ jobId }: CommentsPageProps) {
  const { user } = useAuth();
  const [comments, setComments] = useState<CommentRecord[]>([]);
  const [mentionable, setMentionable] = useState<MentionableUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  // Active @-query: the caret position of the "@" plus the text after it.
  const [mention, setMention] = useState<{ at: number; query: string } | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    Promise.all([getJobComments(jobId), getMentionableUsers(jobId)])
      .then(([commentResult, mentionableResult]) => {
        if (cancelled) return;
        setComments(commentResult.comments);
        setMentionable(mentionableResult.users);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : "Could not load comments");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  // Filtered candidate list for the open query, best names first.
  const mentionMatches = useMemo(() => {
    if (!mention) return [];
    const query = mention.query.toLowerCase();
    return mentionable
      .filter(
        (candidate) =>
          !query ||
          candidate.displayName.toLowerCase().includes(query) ||
          candidate.email.toLowerCase().includes(query),
      )
      .slice(0, MENTION_MATCH_LIMIT);
  }, [mention, mentionable]);

  /**
   * Recomputes the active @-query from the caret. An "@" opens a query when it
   * starts the text or follows whitespace, and nothing whitespace-ish follows
   * it up to the caret — so emails in prose never trigger the popup.
   */
  const updateMention = (element: HTMLTextAreaElement): void => {
    const value = element.value;
    const caret = element.selectionStart ?? value.length;
    const upto = value.slice(0, caret);
    const at = upto.lastIndexOf("@");
    if (at === -1) {
      setMention(null);
      return;
    }
    const precededByBoundary = at === 0 || /\s/.test(upto[at - 1]);
    const query = upto.slice(at + 1);
    if (precededByBoundary && !/\s/.test(query)) {
      setMention((previous) =>
        previous && previous.at === at && previous.query === query ? previous : { at, query },
      );
      setMentionIndex(0);
    } else {
      setMention(null);
    }
  };

  const handleBodyChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setBody(event.target.value);
    updateMention(event.target);
  };

  const handleCaretMove = (event: React.SyntheticEvent<HTMLTextAreaElement>): void => {
    updateMention(event.currentTarget);
  };

  /** Replaces the open @-query with the full mention token plus a space. */
  const insertMention = (candidate: MentionableUser): void => {
    const element = textareaRef.current;
    if (!element || !mention) return;
    const token = `@[${candidate.displayName}](user:${candidate.id})`;
    const caret = element.selectionStart ?? element.value.length;
    const next = element.value.slice(0, mention.at) + token + " " + element.value.slice(caret);
    setBody(next);
    setMention(null);
    const nextCaret = mention.at + token.length + 1;
    requestAnimationFrame(() => {
      element.focus();
      element.setSelectionRange(nextCaret, nextCaret);
    });
  };

  /** Wraps the selection (or drops a bare marker pair) with a markdown marker. */
  const wrapSelection = (marker: string): void => {
    const element = textareaRef.current;
    if (!element) return;
    const { selectionStart: start, selectionEnd: end, value } = element;
    const selected = value.slice(start, end);
    setBody(value.slice(0, start) + marker + selected + marker + value.slice(end));
    const selStart = start + marker.length;
    const selEnd = selStart + selected.length;
    requestAnimationFrame(() => {
      element.focus();
      element.setSelectionRange(selStart, selEnd);
    });
  };

  /** Prefixes every line touching the selection (the list button). */
  const prefixSelectionLines = (): void => {
    const element = textareaRef.current;
    if (!element) return;
    const value = element.value;
    const start = value.lastIndexOf("\n", Math.max(element.selectionStart ?? 0, 1) - 1) + 1;
    const lineEnd = value.indexOf("\n", element.selectionEnd ?? value.length);
    const stop = lineEnd === -1 ? value.length : lineEnd;
    const block = value.slice(start, stop);
    const prefixed = block
      .split("\n")
      .map((line) => (line.trim() ? `- ${line}` : line))
      .join("\n");
    setBody(value.slice(0, start) + prefixed + value.slice(stop));
    requestAnimationFrame(() => {
      element.focus();
      element.setSelectionRange(start, start + prefixed.length);
    });
  };

  /**
   * Toolbar buttons must not steal the textarea's selection on press, or the
   * wrap target would be gone before the click lands.
   */
  const handleToolMouseDown = (event: MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
  };

  const submit = async (): Promise<void> => {
    const text = body.trim();
    if (!text || sending) return;
    setSending(true);
    setSendError("");
    try {
      const { comment } = await createJobComment(jobId, text);
      setComments((previous) => [...previous, comment]);
      setBody("");
      setMention(null);
    } catch (cause: unknown) {
      setSendError(cause instanceof Error ? cause.message : "Could not post the comment");
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (mention && mentionMatches.length > 0) {
      const last = mentionMatches.length - 1;
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        const delta = event.key === "ArrowDown" ? 1 : -1;
        setMentionIndex((index) => (index + delta + mentionMatches.length) % mentionMatches.length);
        return;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        insertMention(mentionMatches[Math.min(mentionIndex, last)]);
        return;
      }
      if (event.key === "Escape") {
        // Only the popup closes; the job modal's document-level Escape handler
        // must never see this event.
        event.stopPropagation();
        setMention(null);
        return;
      }
    }
    if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
      event.preventDefault();
      void submit();
    }
  };

  const handleDelete = (comment: CommentRecord): void => {
    // Two-step confirm (the persisted-attachment pattern): the first click
    // arms, the second deletes, focus loss disarms.
    if (pendingDeleteId !== comment.id) {
      setPendingDeleteId(comment.id);
      return;
    }
    setPendingDeleteId(null);
    deleteJobComment(jobId, comment.id)
      .then(() => {
        setComments((previous) => previous.filter((entry) => entry.id !== comment.id));
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not delete the comment");
      });
  };

  return (
    <section className={`${form.section} ${styles.root}`}>
      <h3 className={form.sectionTitle}>Comments</h3>

      {error && <p className={styles.error}>{error}</p>}
      {loading && <p className={styles.empty}>Loading comments…</p>}
      {!loading && comments.length === 0 && !error && (
        <p className={styles.empty}>No comments yet — start the conversation.</p>
      )}

      {!loading && comments.length > 0 && (
        <ol className={styles.list}>
          {comments.map((comment) => (
            <li key={comment.id} className={styles.item}>
              <div className={styles.itemHead}>
                <span className={styles.author}>{comment.authorName || comment.authorEmail || "Unknown"}</span>
                <span className={styles.date}>{formatAttachmentDate(comment.createdAt)}</span>
                {comment.authorId === user?.id && (
                  <button
                    type="button"
                    className={pendingDeleteId === comment.id ? styles.deleteArmed : styles.delete}
                    onClick={() => handleDelete(comment)}
                    onBlur={() => setPendingDeleteId(null)}
                    title={pendingDeleteId === comment.id ? "Click again to delete" : "Delete comment"}
                  >
                    {pendingDeleteId === comment.id ? "Confirm delete" : "Delete"}
                  </button>
                )}
              </div>
              <Markdown source={comment.body} />
            </li>
          ))}
        </ol>
      )}

      <div className={styles.composer}>
        <div className={styles.toolbar} role="group" aria-label="Formatting">
          <button
            type="button"
            className={styles.tool}
            onMouseDown={handleToolMouseDown}
            onClick={() => wrapSelection("**")}
            title="Bold"
          >
            B
          </button>
          <button
            type="button"
            className={`${styles.tool} ${styles.toolItalic}`}
            onMouseDown={handleToolMouseDown}
            onClick={() => wrapSelection("*")}
            title="Italic"
          >
            I
          </button>
          <button
            type="button"
            className={`${styles.tool} ${styles.toolCode}`}
            onMouseDown={handleToolMouseDown}
            onClick={() => wrapSelection("`")}
            title="Inline code"
          >
            {"</>"}
          </button>
          <button
            type="button"
            className={styles.tool}
            onMouseDown={handleToolMouseDown}
            onClick={prefixSelectionLines}
            title="Bulleted list"
          >
            •≡
          </button>
        </div>

        <div className={styles.editorArea}>
          <textarea
            ref={textareaRef}
            className={styles.input}
            value={body}
            rows={3}
            onChange={handleBodyChange}
            onKeyDown={handleKeyDown}
            onClick={handleCaretMove}
            onSelect={handleCaretMove}
            placeholder="Write a comment… type @ to mention someone"
            aria-label="Write a comment"
          />
          {mention && mentionMatches.length > 0 && (
            <ul className={styles.mentionList} role="listbox" aria-label="Mention someone">
              {mentionMatches.map((candidate, index) => (
                <li key={candidate.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={index === mentionIndex}
                    className={index === mentionIndex ? styles.mentionActive : styles.mentionItem}
                    onMouseEnter={() => setMentionIndex(index)}
                    onMouseDown={handleToolMouseDown}
                    onClick={() => insertMention(candidate)}
                  >
                    <span className={styles.mentionName}>{candidate.displayName}</span>
                    <span className={styles.mentionEmail}>
                      {candidate.kind === "portal" ? `${candidate.email} · portal` : candidate.email}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className={styles.composerFoot}>
          {sendError && <p className={styles.error}>{sendError}</p>}
          <span className={styles.hint}>**bold** · *italic* · `code` · - list · Ctrl+Enter posts</span>
          <button
            type="button"
            className={styles.submit}
            onClick={() => void submit()}
            disabled={sending || body.trim().length === 0}
          >
            {sending ? "Posting…" : "Comment"}
          </button>
        </div>
      </div>
    </section>
  );
}
